import apiClient from './meta-client';
import type {
    EsteiraGroup,
    EsteiraGroupCreateInput,
    EsteiraGroupUpdateInput,
    EsteiraGroupListResponse,
    EsteiraBroadcastListResponse,
    EsteiraBroadcastStatus,
    EsteiraGroupStats,
    EsteiraPreviewResponse,
    EsteiraStatus,
    EsteiraFormOptionsResponse,
    EsteiraPreviewCountRequest,
    EsteiraPreviewCountResponse,
} from '@/types/esteira';

// ─── Form options (projects + domains + pages consolidado) ─────────

export const getEsteiraFormOptions = async (): Promise<EsteiraFormOptionsResponse> => {
    const response = await apiClient.get<EsteiraFormOptionsResponse>(
        '/esteira/form-options',
    );
    return response.data;
};

/** Conta quantos templates voltariam pros filtros propostos (sem criar grupo). */
export const previewEsteiraCount = async (
    body: EsteiraPreviewCountRequest,
): Promise<EsteiraPreviewCountResponse> => {
    const response = await apiClient.post<EsteiraPreviewCountResponse>(
        '/esteira/preview-count',
        body,
    );
    return response.data;
};

// ─── Grupos ─────────────────────────────────────────────────────────

export const createEsteiraGroup = async (
    data: EsteiraGroupCreateInput,
): Promise<EsteiraGroup> => {
    const response = await apiClient.post<EsteiraGroup>('/esteira/groups', data);
    return response.data;
};

export const listEsteiraGroups = async (params?: {
    status?: EsteiraStatus;
    target_country?: string;
    page?: number;
    per_page?: number;
}): Promise<EsteiraGroupListResponse> => {
    const response = await apiClient.get<EsteiraGroupListResponse>(
        '/esteira/groups',
        { params },
    );
    return response.data;
};

export const getEsteiraGroup = async (id: number): Promise<EsteiraGroup> => {
    const response = await apiClient.get<EsteiraGroup>(`/esteira/groups/${id}`);
    return response.data;
};

export const updateEsteiraGroup = async (
    id: number,
    data: EsteiraGroupUpdateInput,
): Promise<EsteiraGroup> => {
    const response = await apiClient.patch<EsteiraGroup>(`/esteira/groups/${id}`, data);
    return response.data;
};

export const pauseEsteiraGroup = async (id: number): Promise<EsteiraGroup> => {
    const response = await apiClient.post<EsteiraGroup>(`/esteira/groups/${id}/pause`);
    return response.data;
};

export const resumeEsteiraGroup = async (id: number): Promise<EsteiraGroup> => {
    const response = await apiClient.post<EsteiraGroup>(`/esteira/groups/${id}/resume`);
    return response.data;
};

export const archiveEsteiraGroup = async (id: number): Promise<EsteiraGroup> => {
    const response = await apiClient.post<EsteiraGroup>(`/esteira/groups/${id}/archive`);
    return response.data;
};

// ─── Broadcasts + Stats ──────────────────────────────────────────────

export const listEsteiraBroadcasts = async (
    groupId: number,
    params?: {
        status?: EsteiraBroadcastStatus;
        /** CSV: 'active' | 'error_retry' | 'active,error_retry'. Omitido = todos.
         *  Filtra no SERVIDOR — filtrar no cliente quebraria a paginação
         *  (devolveria menos itens que per_page e o total não bateria). */
        lead_filter?: string;
        page?: number;
        per_page?: number;
    },
): Promise<EsteiraBroadcastListResponse> => {
    const response = await apiClient.get<EsteiraBroadcastListResponse>(
        `/esteira/groups/${groupId}/broadcasts`,
        { params },
    );
    return response.data;
};

// ─── Upcoming slots (1 linha por horário+template, agregado) ───────

export interface UpcomingSlotPage {
    page_id: number;
    broadcast_id: number;
    /** Nome da página (backend resolve sem filtrar is_active — mostra mesmo
     *  se page foi desativada depois do agendamento). */
    page_name?: string | null;
    /** Page id real do Facebook (string). */
    fb_page_id?: string | null;
}

export interface UpcomingSlot {
    scheduled_at: string;
    template_intel_id: number;
    /** 'active' (default) = leads is_active=1; 'error_retry' = slot adicional
     *  pra leads is_active=0 com last_error_code em group.retry_error_codes.
     *  Backend agrupa SEPARADO de active mesmo compartilhando template. */
    lead_filter?: 'active' | 'error_retry';
    page_count: number;
    pages: UpcomingSlotPage[];
    /** Sample payload — todas as páginas do slot compartilham (synchronized) ou
     *  cada (slot,template) tem seu (per_page_random). Usado pelo preview. */
    message_payload?: any;
    applied_buttons?: any;
    /** Preview do template de fallback pré-selecionado (rotaciona por broadcast).
     *  Só vem preenchido em grupos com pasta de fallback. */
    fallback_template_id?: number | null;
    fallback_template_name?: string | null;
    fallback_message_payload?: any;
}

export interface UpcomingSlotsResponse {
    items: UpcomingSlot[];
    /** Total de broadcasts pendentes (todos os horários). */
    total_pending: number;
    /** Nº de horário+template distintos no total (sem o LIMIT). */
    total_groups: number;
}

export const listEsteiraUpcomingSlots = async (
    groupId: number,
    limit: number = 50,
    /** CSV: 'active' | 'error_retry'. Omitido = todos. Filtra no SERVIDOR —
     *  o `limit` é do servidor, então filtrar no cliente devolveria menos
     *  slots que o pedido e os totais não bateriam. */
    leadFilter?: string,
): Promise<UpcomingSlotsResponse> => {
    const response = await apiClient.get<UpcomingSlotsResponse>(
        `/esteira/groups/${groupId}/upcoming-slots`,
        { params: { limit, lead_filter: leadFilter } },
    );
    return response.data;
};

export const getEsteiraGroupStats = async (
    groupId: number,
): Promise<EsteiraGroupStats> => {
    const response = await apiClient.get<EsteiraGroupStats>(
        `/esteira/groups/${groupId}/stats`,
    );
    return response.data;
};

/** Proxy pro /preview do ms-template-intelligence — UI mostra o que vai sair. */
export const previewEsteiraGroup = async (
    groupId: number,
    n: number = 10,
): Promise<EsteiraPreviewResponse> => {
    const response = await apiClient.get<EsteiraPreviewResponse>(
        `/esteira/groups/${groupId}/preview`,
        { params: { n } },
    );
    return response.data;
};

/** Dispara replenish síncrono (sem esperar o beat de 5min).
 *  Hoje sempre limpa os pendentes antes de re-popular (regenera fila do zero). */
export interface EsteiraReplenishNowResponse {
    success: boolean;
    inserted: number;
    requested: number;
    deleted_pending?: number;
    reason?: string | null;
}

export const replenishEsteiraGroupNow = async (
    groupId: number,
): Promise<EsteiraReplenishNowResponse> => {
    const response = await apiClient.post<EsteiraReplenishNowResponse>(
        `/esteira/groups/${groupId}/replenish-now`,
    );
    return response.data;
};

// ─── Edit broadcasts pending (em lote) ───────────────────────────

export interface EsteiraBroadcastButtonEdit {
    title: string;
    url: string;
}

export interface EsteiraBroadcastBatchEditPayload {
    broadcast_ids: number[];
    title?: string;
    subtitle?: string;
    image_url?: string;
    buttons?: EsteiraBroadcastButtonEdit[];
}

export interface EsteiraBroadcastBatchEditResponse {
    updated: number;
    skipped: number;
}

export const batchEditEsteiraBroadcasts = async (
    payload: EsteiraBroadcastBatchEditPayload,
): Promise<EsteiraBroadcastBatchEditResponse> => {
    const response = await apiClient.patch<EsteiraBroadcastBatchEditResponse>(
        '/esteira/broadcasts/batch-edit',
        payload,
    );
    return response.data;
};

// ─── Gerenciar pages do grupo ───────────────────────────────

export interface EsteiraSyncFromCategoriesResult {
    added: number;
    skipped_existing: number;
    skipped_no_permission: number;
    total_eligible: number;
    group: EsteiraGroup;
}

export const addPageToEsteiraGroup = async (
    groupId: number, pageId: number,
): Promise<EsteiraGroup> => {
    const r = await apiClient.post<EsteiraGroup>(
        `/esteira/groups/${groupId}/pages`, { page_id: pageId },
    );
    return r.data;
};

export const removePageFromEsteiraGroup = async (
    groupId: number, pageId: number,
): Promise<EsteiraGroup> => {
    const r = await apiClient.delete<EsteiraGroup>(
        `/esteira/groups/${groupId}/pages/${pageId}`,
    );
    return r.data;
};

export const syncEsteiraGroupFromCategories = async (
    groupId: number, categoryIds: number[],
): Promise<EsteiraSyncFromCategoriesResult> => {
    const r = await apiClient.post<EsteiraSyncFromCategoriesResult>(
        `/esteira/groups/${groupId}/sync-from-categories`,
        { category_ids: categoryIds },
    );
    return r.data;
};

export interface EsteiraBatchRemovePagesResult {
    removed: number;
    skipped_not_in_group: number;
    skipped_no_permission: number;
    group: EsteiraGroup;
}

export const batchRemovePagesFromEsteiraGroup = async (
    groupId: number, pageIds: number[],
): Promise<EsteiraBatchRemovePagesResult> => {
    const r = await apiClient.post<EsteiraBatchRemovePagesResult>(
        `/esteira/groups/${groupId}/remove-pages`,
        { page_ids: pageIds },
    );
    return r.data;
};
